import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";

export const NAV_LINKS = [
  { to: "/", label: "Αρχική" },
  { to: "/ekdoseis", label: "Εκδόσεις" },
  { to: "/ergaleia", label: "Ψηφιακά Εργαλεία" },
  { to: "/seminaria", label: "Σεμινάρια" },
  { to: "/arthra", label: "Άρθρα" },
  { to: "/gia-emas", label: "Για εμάς" },
];

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,box-shadow] duration-500 ${
        scrolled && !open ? "bg-cream/85 backdrop-blur-xl shadow-[0_1px_0_rgba(13,27,42,0.1)]" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 sm:h-20">
          <Link to="/" data-testid="logo-link" className={`font-display text-xl sm:text-2xl font-bold tracking-tight relative z-50 transition-colors duration-300 ${open ? "text-cream" : "text-ink"}`}>
            Noetica <span className="text-terracotta italic">Lab</span>
          </Link>

          <nav className="hidden lg:flex items-center gap-7" data-testid="desktop-nav">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                data-testid={`nav-link-${l.to === "/" ? "home" : l.to.slice(1)}`}
                className={({ isActive }) =>
                  `relative text-sm font-medium tracking-wide transition-colors duration-300 hover:text-terracotta ${
                    isActive ? "text-terracotta" : "text-ink"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <Link
              to="/epikoinonia"
              data-testid="nav-contact-btn"
              className="group inline-flex items-center gap-1.5 bg-ink text-cream text-sm font-medium pl-5 pr-4 py-2.5 rounded-full transition-colors duration-300 hover:bg-terracottaD"
            >
              Επικοινωνία
              <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </nav>

          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            className={`lg:hidden relative z-50 w-10 h-10 inline-flex items-center justify-center transition-colors duration-300 ${open ? "text-cream" : "text-ink"}`}
            aria-label="Menu"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-40 bg-ink text-cream lg:hidden flex flex-col justify-center px-8"
            data-testid="mobile-menu"
          >
            <nav className="space-y-2">
              {NAV_LINKS.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, x: -24 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.08 + i * 0.06, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                >
                  <NavLink to={l.to} end={l.to === "/"} className="block font-display text-3xl font-bold py-2 hover:text-terracotta transition-colors duration-300">
                    <span className="font-mono2 text-xs text-terracotta align-super mr-3">{String(i + 1).padStart(2, "0")}</span>
                    {l.label}
                  </NavLink>
                </motion.div>
              ))}
              <motion.div initial={{ opacity: 0, x: -24 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.5, duration: 0.5 }}>
                <NavLink to="/epikoinonia" className="block font-display text-3xl font-bold py-2 text-terracotta">
                  <span className="font-mono2 text-xs align-super mr-3">07</span>
                  Επικοινωνία
                </NavLink>
              </motion.div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
